import React, { useEffect } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useAtom } from 'jotai';
import { Outlet, useLocation } from 'react-router-dom';

import {
  accountHomeBaseUrl,
  useAdobeDataLayer,
  useMaintenanceMode,
  useTracking,
  useWebsiteUpdatesBanner,
} from '@consumer-frontend/nc-api';
import {
  accountIdAtom,
  NcFooter,
  NcHeader,
  NcModal,
  NcNav,
  NcWebsiteUpdatesBanner,
} from '@consumer-frontend/nc-components';
import {
  mergeClassNameStrings,
  toCamelCase,
  TrackPageActionPaymentT,
} from '@consumer-frontend/nc-utils';
import { Container } from '@consumer-frontend/shared-ui';

import { analytics } from '../utils/adobe-analytics';

import '@consumer-bootstrap/netcredit.scss';

export const RootPage: React.FC = () => {
  useMaintenanceMode();

  const location = useLocation();
  const [accountId] = useAtom(accountIdAtom);
  const [isWebsiteUpdatesBannerOpen] = useWebsiteUpdatesBanner();
  const {
    adobeDataLayer,
    error: adobeError,
    isLoading,
  } = useAdobeDataLayer(accountId);
  const { trackPage } = useTracking(accountId);

  // Fire trackPage event on route change
  useEffect(
    () => {
      try {
        analytics.track('routingChange', { to_path: location.pathname });

        trackPage({
          tracked_action: toCamelCase(location.pathname.replace(/^\//, '')) as TrackPageActionPaymentT,
          tracked_controller: 'reactRouter',
          tracked_url: `${accountHomeBaseUrl}${location.pathname}`,
        });
      } catch {
        // Swallow tracking errors
      }
    },
    [location.pathname, trackPage],
  );

  // Dispatch Adobe data
  useEffect(() => {
    // Wait for data to be loaded.
    if (isLoading) { return; }

    if (adobeError) {
      datadogRum.addError(adobeError);

      return;
    }

    if (adobeDataLayer) {
      analytics.identify(adobeDataLayer.accountId, { ...adobeDataLayer });
      // Note this is not the same `track` function defined in utils/adobe-analytics;
      // Adobe normalizes args passed here and calls it internally.
      analytics.track('adobeDataLayerLoaded', { ...adobeDataLayer });
    }
  }, [
    adobeDataLayer,
    adobeError,
    isLoading,
  ]);

  // Attach the account id to the RUM session so errors and events are identifiable
  useEffect(() => {
    if (accountId) {
      datadogRum.setUser({ id: accountId });
    }
  }, [accountId]);

  return (
    <>
      <NcHeader />

      <Container
        className={mergeClassNameStrings(
          'root-container d-flex',
          isWebsiteUpdatesBannerOpen && 'website-updates-banner-visible',
        )}
      >
        <NcNav displayNone id="nc-nav" responsiveFlexClass="d-md-flex" hasBoxShadow />

        <Outlet />

        <NcWebsiteUpdatesBanner />
      </Container>

      <NcFooter />
      <NcModal />
    </>
  );
};
